import { Controller } from '@hotwired/stimulus';
import consumer from '../channels/consumer';
import {
  embedSignature,
  onSignliftEvent,
  finalizeSignature,
  SIGNLIFT_EVENTS,
} from '../lib/signlift';

const READY_STEP = 2;
const SIGNATURE_STEP = 3;

export default class SignatureController extends Controller {
  static targets = [
    'waiting',
    'ready',
    'unavailable',
    'explanation',
    'signature',
    'confirmation',
    'error',
    'budgetNotice',
    'progress',
    'loadingLabel',
    'iframeContainer',
  ];

  static values = {
    channelParams: Object,
    finalizeUrl: String,
    signedRedirectUrl: String,
    awaitingFinalizationText: String,
    iframeTitle: String,
    stepCount: { type: Number, default: 4 },
    autoStart: { type: Boolean, default: true },
    errorDelay: { type: Number, default: 5000 },
    contractDeadline: { type: Number, default: 180000 },
  };

  connect() {
    this.contractDelivered = false;
    this.finalizeInflight = false;
    this.cleanupSignlift = null;

    if (this.autoStartValue) this.start();
  }

  disconnect() {
    clearTimeout(this.errorTimer);
    clearTimeout(this.deadlineTimer);
    this.cleanupSignlift?.();
    this.stopListening();
  }

  start() {
    if (this.channel) return;

    this.#show(this.waitingTargets);
    this.deadlineTimer = setTimeout(() => this.#giveUpOnContract(), this.contractDeadlineValue);

    this.channel = consumer.subscriptions.create(this.channelParamsValue, {
      received: this.#received.bind(this),
      rejected: () => {
        this.stopListening();
        this.#showErrorLater();
      },
    });
  }

  stopListening() {
    if (!this.channel) return;

    consumer.subscriptions.remove(this.channel);
    this.channel = null;
  }

  showSignatureStep() {
    this.#hide(this.waitingTargets, this.readyTargets);
    this.#show(this.signatureTargets);
    this.#setProgress(SIGNATURE_STEP);
  }

  awaitFinalization() {
    this.contractDelivered = true;
    clearTimeout(this.errorTimer);
    clearTimeout(this.deadlineTimer);

    if (this.hasLoadingLabelTarget && this.awaitingFinalizationTextValue) {
      this.loadingLabelTarget.textContent = this.awaitingFinalizationTextValue;
    }

    this.#hide(
      this.explanationTargets,
      this.readyTargets,
      this.signatureTargets,
      this.errorTargets,
      this.confirmationTargets,
    );
    this.#show(this.waitingTargets);
    this.#setProgress(SIGNATURE_STEP);
  }

  advanceToConfirmation() {
    this.contractDelivered = true;
    clearTimeout(this.errorTimer);
    clearTimeout(this.deadlineTimer);

    if (this.signedRedirectUrlValue) {
      window.location.assign(this.signedRedirectUrlValue);
      return;
    }

    this.#hide(this.#panels);
    this.#show(this.confirmationTargets);
    this.#setProgress(this.stepCountValue);
  }

  #received(data) {
    try {
      if (data.status === 'success') {
        this.contractDelivered = true;
        clearTimeout(this.deadlineTimer);

        if (data.finalize_url) this.finalizeUrlValue = data.finalize_url;

        this.#embedDocument(data);
        this.#displayReadyStep();
      } else if (data.status === 'signed') {
        this.advanceToConfirmation();
      } else if (data.status === 'awaiting_finalization') {
        this.awaitFinalization();
      } else {
        this.contractDelivered = true;
        this.#showErrorLater();
      }
    } catch {
      this.contractDelivered = true;
      this.#showErrorLater();
    }
  }

  // The tunnel only leaves the waiting panel on a broadcast, so a document
  // pipeline that never answers would otherwise spin forever.
  #giveUpOnContract() {
    if (this.contractDelivered) return;

    this.stopListening();

    if (!this.hasUnavailableTarget) {
      this.#showErrorLater();
      return;
    }

    this.#hide(this.waitingTargets, this.readyTargets);
    this.#show(this.unavailableTargets);
  }

  #embedDocument(data) {
    if (data.signing_url) {
      this.#embedSigningSession(data.signing_url);
    } else if (data.document_url) {
      this.#embedContractPreview(data.document_url);
    }
  }

  #embedSigningSession(signingUrl) {
    if (!this.hasIframeContainerTarget) return;

    const iframe = embedSignature(this.iframeContainerTarget, signingUrl, this.iframeTitleValue);

    this.cleanupSignlift?.();
    this.finalizeInflight = false;
    this.cleanupSignlift = onSignliftEvent(
      ({ type }) => {
        if (type === SIGNLIFT_EVENTS.SUCCESS) {
          this.#triggerFinalize();
        } else if (type === SIGNLIFT_EVENTS.ERROR) {
          this.#showError();
        }
      },
      signingUrl,
      iframe,
    );
  }

  #embedContractPreview(documentUrl) {
    const iframe = document.createElement('iframe');
    iframe.src = documentUrl;
    iframe.style.width = '100%';
    iframe.style.height = '100%';

    this.signatureTarget.prepend(iframe);
  }

  #triggerFinalize() {
    if (!this.finalizeUrlValue || this.finalizeInflight) return;

    this.finalizeInflight = true;

    if (this.signedRedirectUrlValue) {
      this.awaitFinalization();
    } else {
      this.advanceToConfirmation();
    }

    finalizeSignature(this.finalizeUrlValue)
      .then((response) => {
        if (!response.ok || response.redirected) return this.#showError();
        if (!this.signedRedirectUrlValue || !this.element.isConnected) return;

        window.location.assign(this.signedRedirectUrlValue);
      })
      .catch(() => this.#showError());
  }

  #displayReadyStep() {
    if (this.hasReadyTarget) {
      this.#hide(this.waitingTargets, this.confirmationTargets);
      this.#show(this.readyTargets);
    }

    this.#setProgress(READY_STEP);
  }

  #showErrorLater() {
    clearTimeout(this.errorTimer);
    this.errorTimer = setTimeout(() => this.#showError(), this.errorDelayValue);
  }

  #showError() {
    clearTimeout(this.errorTimer);
    this.#hide(this.#panels);
    this.#show(this.errorTargets);
    this.#setProgress(SIGNATURE_STEP);
  }

  get #panels() {
    return [
      this.waitingTargets,
      this.readyTargets,
      this.unavailableTargets,
      this.signatureTargets,
      this.confirmationTargets,
      this.errorTargets,
      this.budgetNoticeTargets,
    ];
  }

  #hide(...groups) {
    groups.flat().forEach((element) => element.classList.add('d-none'));
  }

  #show(elements) {
    elements.forEach((element) => element.classList.remove('d-none'));
  }

  #setProgress(step) {
    if (!this.hasProgressTarget) return;

    this.progressTarget.textContent = `${step}/${Math.max(step, this.stepCountValue)}`;
  }
}
